// Per-tab impersonation handoff — MUST run before the auth store (and anything
// that imports it) so the store's persist storage selector can pick sessionStorage
// for an impersonation tab. Side-effecting import; keep it first.
import './lib/impersonation-bootstrap';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { queryClient } from './lib/query';
import { routeTree } from './routeTree.gen';
import { initSentry, SentryErrorBoundary } from './lib/sentry';
import { initBrowserTelemetry } from './lib/telemetry';
import { applyBranding } from './lib/branding/apply';
import { readSnapshot, writeSnapshot } from './features/site-config/snapshot';
import { fetchSiteConfig, siteConfigHost } from './features/site-config/api';
import { useBrandingStore } from './stores/branding';
import { beginTenantResolution, settleTenantResolution, useTenantStore } from './stores/tenant';
import { initChatwoot } from './lib/chatwoot';
import { usePrefsStore } from './stores/prefs';
import { storageKey } from './lib/storage-key';
import { initHostPool, refreshHostList, HOST_LIST_REFRESH_MS } from './lib/api/host-pool';
import { affCodeFromSearch, rememberAffCode } from './lib/aff-code';
import i18n from './lib/i18n';
import './styles/globals.css';

initSentry();
initBrowserTelemetry();

// Synchronous boot: apply the cached snapshot for this host before React mounts
// (zero-network first paint, no FOUC). Neutral globals.css defaults stand in
// when there is no snapshot yet.
const host = siteConfigHost();
const snapshot = readSnapshot(host);
if (snapshot) {
  applyBranding(snapshot);
  useBrandingStore.setState({ branding: snapshot });
  useTenantStore.getState().setTenant(snapshot.tenantId);
}

// No snapshot on a first visit: hold tenant-scoped requests until /site-config
// names the brand, so they are not answered for no brand at all.
beginTenantResolution();

// Read before the router can rewrite the URL; stored once the tenant settles.
const affCode = affCodeFromSearch(window.location.search);

// The stored API host list is re-verified before the first request, so a host an
// operator added is already a failover candidate for it; then kept fresh.
const hostPoolReady = initHostPool();
void hostPoolReady.then(refreshHostList);
setInterval(() => void refreshHostList(), HOST_LIST_REFRESH_MS);

// Revalidate in the background; re-apply + write the snapshot back on success.
void hostPoolReady
  .then(fetchSiteConfig)
  .then((cfg) => {
    applyBranding(cfg);
    useBrandingStore.setState({ branding: cfg });
    useTenantStore.getState().setTenant(cfg.tenantId);
    writeSnapshot(host, cfg);
    if (cfg.defaultLocale) {
      const userLocaleSet = (() => {
        try {
          return Boolean(localStorage.getItem(storageKey('locale')));
        } catch {
          return false;
        }
      })();
      if (!userLocaleSet) {
        void i18n.changeLanguage(cfg.defaultLocale);
        usePrefsStore.setState({ locale: cfg.defaultLocale });
      }
    }
    initChatwoot(cfg.chatwoot, usePrefsStore.getState().locale);
  })
  .catch(() => {
    // non-fatal: neutral defaults + cached snapshot (if any) remain
    initChatwoot(null, usePrefsStore.getState().locale);
  })
  .finally(() => {
    settleTenantResolution();
    if (affCode) rememberAffCode(affCode);
  });

/**
 * Without a `defaultErrorComponent` TanStack resolves each match's boundary to
 * `SafeFragment`, so a render throw anywhere in any route travels to the root
 * `SentryErrorBoundary` and replaces the entire application — nav, balance pill
 * and all. Setting one degrades a broken route to a message inside the shell,
 * which is the difference between a bad screen and a dead session (WEB-22).
 */
const router = createRouter({ routeTree, defaultErrorComponent: RouteErrorFallback });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

function RouteErrorFallback() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 16px', gap: '16px', fontFamily: 'sans-serif' }}>
      <p style={{ fontSize: '16px', color: '#374151' }}>Something went wrong on this page.</p>
      <button
        onClick={() => window.location.reload()}
        style={{ padding: '8px 24px', borderRadius: '6px', background: '#4f46e5', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '14px' }}
      >
        Reload
      </button>
    </div>
  );
}

function ErrorFallback() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', gap: '16px', fontFamily: 'sans-serif' }}>
      <p style={{ fontSize: '18px', color: '#374151' }}>Something went wrong. Please try again.</p>
      <button
        onClick={() => window.location.reload()}
        style={{ padding: '8px 24px', borderRadius: '6px', background: '#4f46e5', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '14px' }}
      >
        Reload
      </button>
    </div>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <SentryErrorBoundary fallback={<ErrorFallback />}>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </SentryErrorBoundary>
  </StrictMode>,
);
